import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar3 } from 'react-bootstrap-icons';
import { useSearchParams } from 'react-router-dom';
import { PageBusyLoader } from '../ui/PageBusyLoader';
import { ActivityFeedIconChevronLeft, ActivityFeedIconChevronRight } from './activityFeedIcons';
import {
  fetchActivityFeed,
  fetchExternalActivityFeed,
  type ActivityEvent,
} from './activityFeedApi';
import { ActivityCenterRow } from './ActivityCenterRow';
import { useDashboardRealtimeRefresh } from '../../hooks/useDashboardRealtimeRefresh';
import { useAnalyticsStore } from '../../store/analyticsStore';
import { activityEventDomId, findMatchingActivityEvent } from '../../utils/activityDeepLink';
import {
  buildActivityCenterMetrics,
  formatDateRangeLabel,
  getDateRange,
  isEventInRange,
  loadReadActivityIds,
  matchesActivityFilter,
  saveReadActivityIds,
  type ActivityFilterTab,
  type DateRangePreset,
} from '../../utils/activityCenterUtils';
import './Dashboard.css';
import './ActivityCenter.css';

const PAGE_SIZE = 20;
const METRICS_FETCH_SIZE = 120;

const FILTER_TABS: { key: ActivityFilterTab; label: string }[] = [
  { key: 'all', label: 'All Activity' },
  { key: 'views', label: 'Views' },
  { key: 'messages', label: 'Messages' },
  { key: 'questions', label: 'Questions' },
  { key: 'references', label: 'References' },
  { key: 'likes', label: 'Likes' },
];

async function fetchActivityBatch(limit: number, externalOnly = false): Promise<ActivityEvent[]> {
  const all: ActivityEvent[] = [];
  let page = 1;
  const fetchPage = externalOnly ? fetchExternalActivityFeed : fetchActivityFeed;
  while (all.length < limit) {
    const data = await fetchPage(page, Math.min(50, limit));
    all.push(...data.results);
    if (!data.next || data.results.length === 0) break;
    page += 1;
  }
  return all.slice(0, limit);
}

const RecentActivityPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const externalFeedOnly = searchParams.get('feed') === 'external';
  const [allItems, setAllItems] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<ActivityFilterTab>('all');
  const [datePreset, setDatePreset] = useState<DateRangePreset>('7d');
  const [page, setPage] = useState(1);
  const [readIds, setReadIds] = useState<Set<string>>(() => loadReadActivityIds());

  const analytics = useAnalyticsStore((s) => s.candidateAnalytics);
  const fetchCandidateAnalytics = useAnalyticsStore((s) => s.fetchCandidateAnalytics);

  const dateRange = useMemo(() => getDateRange(datePreset), [datePreset]);
  const dateRangeLabel = useMemo(
    () => formatDateRangeLabel(dateRange.start, dateRange.end),
    [dateRange]
  );

  const filteredItems = useMemo(() => {
    return allItems.filter(
      (ev) =>
        matchesActivityFilter(ev.category, filter) &&
        isEventInRange(ev, dateRange.start, dateRange.end)
    );
  }, [allItems, dateRange, filter]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE)),
    [filteredItems.length]
  );

  const pagedItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [filteredItems, page]);

  const metrics = useMemo(
    () => buildActivityCenterMetrics(allItems, analytics, dateRange),
    [allItems, analytics, dateRange]
  );

  const loadAllActivity = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchCandidateAnalytics({ force: false });
      const batch = await fetchActivityBatch(METRICS_FETCH_SIZE, externalFeedOnly);
      setAllItems(batch);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load activity');
      setAllItems([]);
    } finally {
      setLoading(false);
    }
  }, [fetchCandidateAnalytics, externalFeedOnly]);

  useEffect(() => {
    void loadAllActivity();
  }, [loadAllActivity]);

  useEffect(() => {
    setPage(1);
  }, [filter, datePreset]);

  useEffect(() => {
    if (loading || allItems.length === 0) return;

    const highlightId = searchParams.get('highlight');
    const matchMessage = searchParams.get('match');
    const atIso = searchParams.get('at') || undefined;

    let targetId: string | null = highlightId;
    if (!targetId && matchMessage) {
      targetId = findMatchingActivityEvent(allItems, matchMessage, atIso)?.id ?? null;
    }
    if (!targetId) return;

    const matchIndex = filteredItems.findIndex((ev) => ev.id === targetId);
    if (matchIndex >= 0) {
      setPage(Math.floor(matchIndex / PAGE_SIZE) + 1);
    }

    setHighlightedId(targetId);
    requestAnimationFrame(() => {
      document
        .getElementById(activityEventDomId(targetId!))
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [loading, allItems, filteredItems, searchParams]);

  useDashboardRealtimeRefresh(loadAllActivity);

  const unreadCount = useMemo(
    () => filteredItems.filter((ev) => !readIds.has(ev.id)).length,
    [filteredItems, readIds]
  );

  const markAllRead = () => {
    const next = new Set(readIds);
    for (const ev of filteredItems) {
      next.add(ev.id);
    }
    setReadIds(next);
    saveReadActivityIds(next);
  };

  const changeToneClass = (tone: 'up' | 'down' | 'flat') =>
    tone === 'up'
      ? 'ac-metric-change ac-metric-change--up'
      : tone === 'down'
        ? 'ac-metric-change ac-metric-change--down'
        : 'ac-metric-change ac-metric-change--flat';

  if (loading && allItems.length === 0) {
    return <PageBusyLoader pageName="Activity Center" />;
  }

  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="v1-dashboard-page ac-page">
      <main className="v1-dashboard-main ac-main">
        <header className="ac-header">
          <div className="ac-header-copy">
            <h1>{externalFeedOnly ? 'External Profile Activity' : 'Activity Center'}</h1>
            <p>
              {externalFeedOnly
                ? 'See how recruiters and visitors engage with your public profile.'
                : "See who's engaging with your profile and take action."}
            </p>
          </div>
          <label className="ac-date-picker">
            <Calendar3 size={16} className="ac-date-picker-icon" aria-hidden />
            <span>{dateRangeLabel}</span>
            <select
              value={datePreset}
              aria-label="Date range"
              onChange={(e) => setDatePreset(e.target.value as DateRangePreset)}
            >
              <option value="7d">Last 7 days</option>
              <option value="14d">Last 14 days</option>
              <option value="30d">Last 30 days</option>
            </select>
          </label>
        </header>

        {loading ? (
          <div className="ac-skeleton-metrics" aria-hidden>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="ac-skeleton-card" />
            ))}
          </div>
        ) : (
          <section className="ac-metrics" aria-label="Engagement summary">
            <article className="ac-metric-card">
              <p className="ac-metric-label">Profile Views</p>
              <p className="ac-metric-value">{metrics.profileViews}</p>
              <p className={changeToneClass(metrics.profileViewsChange.tone)}>
                {metrics.profileViewsChange.label}
              </p>
            </article>
            <article className="ac-metric-card">
              <p className="ac-metric-label">Messages</p>
              <p className="ac-metric-value">{metrics.messages}</p>
              <p className={changeToneClass(metrics.messagesChange.tone)}>
                {metrics.messagesChange.label}
              </p>
            </article>
            <article className="ac-metric-card">
              <p className="ac-metric-label">Questions</p>
              <p className="ac-metric-value">{metrics.questions}</p>
              <p className={changeToneClass(metrics.questionsChange.tone)}>
                {metrics.questionsChange.label}
              </p>
            </article>
            <article className="ac-metric-card">
              <p className="ac-metric-label">Likes</p>
              <p className="ac-metric-value">{metrics.likes}</p>
              <p className={changeToneClass(metrics.likesChange.tone)}>
                {metrics.likesChange.label}
              </p>
            </article>
            <article className="ac-metric-card ac-metric-card--score">
              <div className="ac-score-row">
                <div>
                  <p className="ac-metric-label">Recruiter Interest Score</p>
                  <p className="ac-metric-value">{metrics.interestScore}/100</p>
                </div>
                <p className={changeToneClass(metrics.interestChange.tone)}>
                  {metrics.interestChange.label}
                </p>
              </div>
              <div className="ac-score-bar">
                <div className="ac-score-bar-fill" style={{ width: `${metrics.interestScore}%` }} />
              </div>
              <p className="ac-score-foot">
                Most viewed section: <strong>{metrics.topSectionLabel}</strong>
              </p>
            </article>
          </section>
        )}

        <div className="ac-filters" role="tablist" aria-label="Activity filters">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={filter === tab.key}
              className={`ac-filter-tab ${filter === tab.key ? 'ac-filter-tab--active' : ''}`}
              onClick={() => setFilter(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <section className="ac-panel">
          <header className="ac-panel-head">
            <h2>Recent Activity</h2>
            <button
              type="button"
              className="ac-mark-all"
              disabled={unreadCount === 0}
              onClick={markAllRead}
            >
              Mark all as read
            </button>
          </header>

          {loading ? (
            <div className="ac-empty">
              <p className="ac-empty-desc">Loading activity…</p>
            </div>
          ) : error ? (
            <div className="ac-empty">
              <p className="ac-empty-title">Could not load activity</p>
              <p className="ac-empty-desc">{error}</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="ac-empty">
              <p className="ac-empty-title">No activity in this period</p>
              <p className="ac-empty-desc">
                When recruiters interact with your profile or you make updates, they will appear
                here.
              </p>
            </div>
          ) : (
            <>
              <ul className="ac-list">
                {pagedItems.map((ev, index) => (
                  <ActivityCenterRow
                    key={ev.id}
                    event={ev}
                    unread={!readIds.has(ev.id)}
                    highlighted={highlightedId === ev.id}
                    isLast={index === pagedItems.length - 1}
                  />
                ))}
              </ul>
              {totalPages > 1 ? (
                <footer className="ac-pagination">
                  <span>
                    Page {page} of {totalPages} · {filteredItems.length} items
                  </span>
                  <div className="ac-pagination-btns">
                    <button
                      type="button"
                      className="ac-pagination-btn"
                      disabled={!canPrev}
                      aria-label="Previous page"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      <ActivityFeedIconChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className="ac-pagination-btn"
                      disabled={!canNext}
                      aria-label="Next page"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      <ActivityFeedIconChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </footer>
              ) : null}
            </>
          )}
        </section>
      </main>
    </div>
  );
};

/** Legacy path alias — same page as v2 `/activity`. */
export function ActivityFeedPage() {
  return <RecentActivityPage />;
}

export default RecentActivityPage;
